import React, { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { feedbackApi } from '../api/feedbackApi';
import appConfig from '../config/appConfig';
import { Star, X, Check } from 'lucide-react';

/**
 * "Rate your experience" widget. Collected privately — nothing is published on
 * its own; a super admin approves/features items and decides whether/where to
 * show them (see FeedbackAdminPage + SocialProof). Rendered only when the
 * feedback config's `collect` is on. Opened via the footer link, which
 * dispatches the `sendero:open-feedback` event.
 */

const COLOR = { primary: '#0D9488', dark: '#0F766E', ink: '#0F172A', muted: '#64748B', border: '#E8ECF0' };

export default function FeedbackWidget() {
  const { user } = useAuth();
  const [collect, setCollect] = useState(() => appConfig.getFeedbackConfig().collect);
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [testimonial, setTestimonial] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => appConfig.subscribe(() => setCollect(appConfig.getFeedbackConfig().collect)), []);
  useEffect(() => {
    const onOpen = () => { setOpen(true); setDone(false); };
    window.addEventListener('sendero:open-feedback', onOpen);
    return () => window.removeEventListener('sendero:open-feedback', onOpen);
  }, []);

  if (!collect) return null;
  if (!open) return null;

  const close = () => { setOpen(false); setRating(0); setHover(0); setComment(''); setTestimonial(''); setConsent(false); setDone(false); };

  const submit = async () => {
    if (!rating) return;
    setBusy(true);
    try {
      await feedbackApi.submit({
        rating,
        comment: comment.trim(),
        testimonial: rating >= 4 ? testimonial.trim() : '',
        consent: rating >= 4 ? consent : false,
        name: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
        org: user?.orgName || user?.organizationName || '',
        role: user?.userRole || user?.role || '',
        page: window.location.pathname,
      });
      setDone(true);
    } catch { setDone(true); /* still thank them; failures shouldn't block */ }
    finally { setBusy(false); }
  };

  return (
    <div role="dialog" aria-label="Rate your experience" style={{ position: 'fixed', inset: 0, zIndex: 9997, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={close}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 460, maxWidth: '100%', background: '#fff', borderRadius: 16, boxShadow: '0 20px 60px rgba(15,23,42,0.28)', overflow: 'hidden' }}>
        <div style={{ background: `linear-gradient(135deg, ${COLOR.dark}, #134E4A)`, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>Rate your experience</div>
          <button onClick={close} aria-label="Close" style={{ border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', width: 30, height: 30, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={16} /></button>
        </div>

        {done ? (
          <div style={{ padding: '32px 24px', textAlign: 'center' }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#ECFDF5', color: '#059669', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}><Check size={26} strokeWidth={2.6} /></div>
            <div style={{ fontSize: 16, fontWeight: 700, color: COLOR.ink }}>Thank you!</div>
            <div style={{ fontSize: 13.5, color: COLOR.muted, marginTop: 6 }}>Your feedback helps us improve the portal.</div>
            <button className="btn-primary" onClick={close} style={{ marginTop: 18 }}>Close</button>
          </div>
        ) : (
          <div style={{ padding: '20px 24px' }}>
            <div style={{ fontSize: 14, color: COLOR.muted, marginBottom: 10 }}>How would you rate the Central Health Provider Portal?</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
              {[1, 2, 3, 4, 5].map((n) => {
                const active = (hover || rating) >= n;
                return (
                  <button key={n} type="button" onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setRating(n)} aria-label={`${n} star${n > 1 ? 's' : ''}`} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 2 }}>
                    <Star size={30} strokeWidth={2} color={active ? '#F59E0B' : '#CBD5E1'} fill={active ? '#F59E0B' : 'none'} />
                  </button>
                );
              })}
            </div>
            <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: COLOR.muted, marginBottom: 6 }}>Comments (optional)</label>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="What worked well, or what could be better?" style={{ width: '100%', border: `1px solid ${COLOR.border}`, borderRadius: 10, padding: '9px 12px', font: 'inherit', fontSize: 14, color: COLOR.ink, outline: 'none', resize: 'vertical' }} />

            {rating >= 4 && (
              <div style={{ marginTop: 14, padding: 12, borderRadius: 10, background: '#F0FDFA', border: `1px solid ${COLOR.primary}22` }}>
                <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: COLOR.dark, marginBottom: 6 }}>Glad it's working for you! Share a testimonial? (optional)</label>
                <textarea value={testimonial} onChange={(e) => setTestimonial(e.target.value)} rows={2} placeholder="e.g. Cut our claim look-up time in half." style={{ width: '100%', border: `1px solid ${COLOR.border}`, borderRadius: 10, padding: '9px 12px', font: 'inherit', fontSize: 14, color: COLOR.ink, outline: 'none', resize: 'vertical', background: '#fff' }} />
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8, fontSize: 12.5, color: COLOR.muted, cursor: 'pointer' }}>
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ marginTop: 2 }} />
                  You may feature this (with my name{user?.orgName ? ' and organization' : ''}) after review.
                </label>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
              <button className="btn-secondary" onClick={close}>Cancel</button>
              <button className="btn-primary" onClick={submit} disabled={!rating || busy}>{busy ? 'Sending…' : 'Submit feedback'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
